/**
 * Converts a Blob object to a base64-encoded string.
 *
 * This function takes a Blob (which could be a File, as File inherits from Blob)
 * and returns a Promise that resolves to the base64 representation of the Blob's contents.
 * It uses FileReader to read the Blob as a data URL and then extracts the base64 part.
 *
 * @param {Blob} blob - The Blob object to convert to base64.
 * @returns {Promise<string>} A Promise that resolves with the base64-encoded string
 *                            representation of the Blob's contents.
 *
 * @throws {Error} If there's an error reading the Blob or converting it to base64.
 *
 * @example
 * // Converting a Blob to base64
 * const myBlob = new Blob(['Hello, world!'], {type: 'text/plain'});
 * blobToBase64(myBlob)
 *   .then(base64String => console.log(base64String))
 *   .catch(error => console.error('Error converting blob to base64:', error));
 */
export const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((onSuccess, onError) => {
        try {
            const reader = new FileReader();

            // eslint-disable-next-line no-inner-declarations
            function onload(): void {
                // @ts-expect-error - invalid type
                onSuccess((this as any)?.result?.split?.(',')?.[1]);
            }

            reader.onload = onload;

            reader.readAsDataURL(blob);
        } catch (e) {
            onError(e);
        }
    });
};
